import * as signalR from '@microsoft/signalr';
import { createListenerMiddleware } from '@reduxjs/toolkit';
import { InfiniteData } from '@tanstack/react-query';
import { truncate } from 'lodash';
import { API_BASE_URL, withAccess, WithAccess } from '../../asyncHelpers';
import { ChatReplyResponse } from '../../sic-lib/api/ai/types/Ai';
import { ChannelItemsResponse } from '../../sic-lib/api/Timeline/types/channelItemsResponse';
import { ChannelType } from '../../sic-lib/enums/ChannelType';
import { AiSmartReplyContactPlanState } from '../../sic-lib/types/Ai';
import { WSMessageEvent, WSMessageStatusChanged } from '../../sic-lib/types/WebsocketEvents';
import { debounce } from '../../sic-lib/utils/throttlingHelpers';
import { filterSelectedChannelTypes } from '../../sic-lib/utils/timelineHelpers';
import { createContactAlertToast, createMessageReceivedToast } from '../../utils/notifyHelpers';
import { queryClient } from '../../utils/reactQuery';
import { appendContactComposeBody } from '../conversation';
import { createToast } from '../notify';
import { AppDispatch, RootState } from '../store';
import { requestConfig } from '../users';
import { setConnectionId } from '../websocket';

export const websocketListenerMiddleware = createListenerMiddleware();
export const startWebsocketListening = websocketListenerMiddleware.startListening.withTypes<
    RootState,
    AppDispatch
>();
//chatgpt can send events to us so quickly, redux cant keep up
//more specifically, our oversubscription to the redux store is hanging the application as they all re-render
//so we debounce the dispatches to the store once a frame (16~ms)
const aiSuggestionBuffer: { [contactId: string]: string } = {};

const debouncedDispatch = debounce((dispatch: AppDispatch, contactId: number, text: string) => {
    dispatch(appendContactComposeBody({ contactId, body: text }));
    aiSuggestionBuffer[contactId] = ''; // Reset the buffer for the contactId
}, 16);

function websocketMiddleware({ access }: WithAccess) {
    let connection: signalR.HubConnection | undefined;

    const setupConnection = async (getState: () => RootState, dispatch: AppDispatch) => {
        const protocol = new signalR.JsonHubProtocol();
        const transport = signalR.HttpTransportType.WebSockets;

        const options = {
            transport,
            logMessageContent: true,
            logger:
                process.env.NODE_ENV === 'development'
                    ? signalR.LogLevel.Debug
                    : signalR.LogLevel.Error,
            accessTokenFactory: () => access.get('access') as string,
        };

        connection = new signalR.HubConnectionBuilder()
            .withUrl(`${API_BASE_URL}/hubs/notifications`, options)
            .withAutomaticReconnect()
            .withHubProtocol(protocol)
            .build();

        try {
            await connection.start();
            //console.log('[WS]: connection started', connection.connectionId);

            dispatch(setConnectionId(connection.connectionId!));
        } catch (e: any) {
            // const errorToast: ToastItem = createErrorToast('Real-time SignalR inbound messages not being received');
            // store.dispatch(createToastAction.createToast(errorToast));
        }

        connection.onreconnected((connectionId?: string) => {
            //console.log('[WS]: connection reconnected', connectionId);
            dispatch(setConnectionId(connectionId!));
        });

        connection.on('ReceiveMessage', (type: string, messageEvent: WSMessageEvent) => {
            console.log('[WS]: ReceiveMessage', type, messageEvent);
            let previewMessage: string = '';

            //decide if we're showing a new toast alert
            switch (messageEvent.channelItem.type) {
                case ChannelType.messageEvents:
                    previewMessage = truncate(messageEvent.channelItem.message, { length: 75 });
                    break;
                case ChannelType.gmailEvents:
                    previewMessage = truncate(messageEvent.channelItem.subject, { length: 75 });
                    break;
                case ChannelType.formRequests:
                case ChannelType.webEvents:
                case ChannelType.facebookEvents:
                case ChannelType.webPushEvents:
                    break;
            }

            if (previewMessage) {
                dispatch(
                    createToast({
                        toastData: createMessageReceivedToast(
                            messageEvent.contact.contactId,
                            `${messageEvent.contact.firstName} ${messageEvent.contact.lastName}`.trim(),
                            messageEvent.channelItem.subType,
                            previewMessage,
                        ),
                    }),
                );
            }

            //refresh new and recent queries
            queryClient.invalidateQueries(['newAndRecent']);

            //attempt to refresh the timeline for the contact
            const state = getState();

            //no channels set - invalidate all data for the contact
            if (!state.conversation?.channels) {
                queryClient.invalidateQueries([`contact-${messageEvent.contact.contactId}`]);
            } else {
                const channels = filterSelectedChannelTypes(state.conversation.channels);
                //attempt optimistic update of the timeline
                const currentTimeline = queryClient.getQueryData<
                    InfiniteData<ChannelItemsResponse>
                >([`contact-${messageEvent.contact.contactId}`, 'timeline', channels]);

                if (currentTimeline) {
                    //its possible, but rare, that the same notification gets processed twice
                    //if we detect a duplicate item, we can ignore it
                    const duplicate = currentTimeline.pages.some(page =>
                        page.items.some(item => item.id === messageEvent.channelItem.id),
                    );
                    if (duplicate) {
                        console.log('Duplicate message detected, ignoring');
                        return;
                    }

                    //add the item to the first item in the first page
                    //also handle the case where their phones suck and havent loaded anything yet,
                    //but theyre trying to send a message anyways. (build a basic page object)
                    let newData: InfiniteData<ChannelItemsResponse>;
                    if (currentTimeline && currentTimeline.pages.length > 0) {
                        newData = {
                            ...currentTimeline,
                            pages: [
                                {
                                    ...currentTimeline.pages[0],
                                    items: [
                                        messageEvent.channelItem,
                                        ...currentTimeline.pages[0].items,
                                    ],
                                },
                                ...currentTimeline.pages.slice(1),
                            ],
                        };
                    } else {
                        newData = {
                            pages: [
                                {
                                    contactId: messageEvent.contact.contactId,
                                    items: [messageEvent.channelItem],
                                    pageNum: 0,
                                },
                            ],
                            pageParams: [],
                        };
                    }

                    queryClient.setQueryData<InfiniteData<ChannelItemsResponse>>(
                        [`contact-${messageEvent.contact.contactId}`, 'timeline', channels],
                        newData,
                    );
                }
            }
        });

        connection.on('SendMessage', (type: string, messageEvent: WSMessageEvent) => {
            //console.log('[WS]: SendMessage', type, messageEvent);
            queryClient.invalidateQueries([
                `contact-${messageEvent.contact.contactId}`,
                'timeline',
            ]);
            queryClient.invalidateQueries(['newAndRecent']);
        });

        connection.on(
            'MessageStatusChanged',
            (type: string, messageEvent: WSMessageStatusChanged) => {
                queryClient.invalidateQueries([`contact-${messageEvent.contactId}`, 'timeline']);
                queryClient.invalidateQueries(['newAndRecent']);
            },
        );

        connection.on('AiChatReply', (type: string, result: ChatReplyResponse) => {
            ////console.log('AiChatReply start', result);
            //handle a case where message is null from chat gpt
            //i think its the "functionCall" part
            if (!result.message === null) return;

            // Append the text to the buffer
            aiSuggestionBuffer[result.contactId] =
                (aiSuggestionBuffer[result.contactId] || '') + result.message;
            debouncedDispatch(dispatch, result.contactId, aiSuggestionBuffer[result.contactId]);
            //store.dispatch(conversationActions.appendAiSuggestion(result.contactId, result.index, result.text));
        });

        connection.on('AiSRContactPlanStateChanged', (state: AiSmartReplyContactPlanState) => {
            //console.log('[WS]: AiSRContactPlanStateChanged', state);
            if (state) {
                queryClient.setQueryData(['aiSmartReplyContactPlanState', state.contactId], state);
            }
        });

        connection.on(
            'ContactAlert',
            (contactId: number, title: string, body: string | undefined) => {
                dispatch(
                    createToast({
                        toastData: createContactAlertToast(contactId, title, body),
                    }),
                );
            },
        );
    };

    const destroyConnection = async () => {
        if (connection) {
            await connection.stop();
        }
    };

    startWebsocketListening({
        actionCreator: requestConfig.fulfilled,
        effect: async (action, { getState, dispatch }) => {
            await setupConnection(getState, dispatch);
        },
    });

    // startWebsocketListening({
    //     actionCreator: CLEAR_APP_STATE,
    //     effect: async (action, listenerApi) => {
    //         await destroyConnection();
    //     },
    // });

    return websocketListenerMiddleware.middleware;
}

export default websocketMiddleware(withAccess());
